/**
 * `guest.ts` — **panggilan tamu lewat QR di meja**.
 *
 * Tamu memindai QR → membuka halaman web → menekan tombol → HP pelayan area itu berbunyi.
 *
 * ### ⚠️⚠️ Ini SATU-SATUNYA jalur tulis TANPA AUTENTIKASI di seluruh sistem
 * Semua endpoint lain menuntut token mesin, dan yang sensitif menuntut token staf. Yang ini
 * terbuka ke internet publik — karena tamu tak punya akun, dan memintanya mendaftar untuk memanggil
 * pelayan adalah cara tercepat membuat fitur ini tak dipakai siapa pun.
 *
 * ⇒ Tiga kontrol menggantikan autentikasi:
 * | Kontrol | Yang ditutup |
 * |---|---|
 * | token QR **tak-tertebak** | tanpa itu `?table=T-05` bisa ditebak, dan orang di rumah bisa memanggil pelayan ke meja 12 seratus kali |
 * | **batas laju** per meja | tamu yang tak segera dihampiri akan menekan berulang; sepuluh bunyi untuk satu kebutuhan membuat pelayan mematikan suaranya |
 * | **nol data pribadi** | tamu tak login, tak diminta nomor telepon, tak ditandai |
 *
 * @module
 */
/**
 * Jenis panggilan. **Dua tombol** (Imron) — bukan satu, bukan lima.
 *
 * ⭐ `bill` dipisahkan karena ia alasan tersering, dan mengetahuinya lebih dulu **menghemat satu
 * perjalanan**: pelayan datang sudah membawa tagihan.
 *
 * ⚠️ Menambah tombol berarti menambah tebakan bagi tamu — dan tamu yang ragu memilih akan
 * memanggil dengan tombol yang salah, sehingga informasinya justru jadi salah.
 */
export const GUEST_CALL_KINDS = Object.freeze(['waiter', 'bill']);
const KIND_SET = new Set(GUEST_CALL_KINDS);
/** Guard {@link GuestCallKind}. */
export function isGuestCallKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
/**
 * Panjang maksimum catatan bebas tamu.
 *
 * ⚠️ **80, dan pendeknya disengaja.** Cukup untuk *"tolong ganti sendok"*; terlalu pendek untuk
 * jadi pesan panjang, keluhan, atau tempat menitipkan nomor telepon. Kotak teks besar di endpoint
 * publik adalah undangan — bagi keluhan yang seharusnya disampaikan langsung, dan bagi yang iseng.
 */
export const GUEST_NOTE_MAX = 80;
/**
 * Panjang token QR meja. 10 karakter dari {@link GUEST_QR_ALPHABET} = **50 bit** — tak bisa ditebak
 * dengan menyapu, dan cukup pendek untuk QR versi 2 yang masih terbaca dari stiker seukuran koin.
 *
 * ⭐ **Opaque, bukan JWT bertanda tangan.** JWT dipilih ketika ingin memverifikasi tanpa menyentuh
 * database — dan di sini itu tak berlaku: kita **tetap** menyentuh DB untuk menulis panggilan dan
 * mengecek batas laju. Jadi ~280 karakter JWT hanya membeli QR yang lebih rapat, yang gagal
 * dipindai di restoran remang oleh kamera berdebu. Ini kendala fisik, bukan selera.
 */
export const GUEST_QR_TOKEN_LENGTH = 10;
/**
 * Base32 Crockford **tanpa `I` `L` `O` `U`.**
 *
 * ⚠️ `I`/`L`/`1` dan `O`/`0` tak terbedakan pada stiker kecil, dan token QR kadang **diketik
 * manual** saat kamera tamu gagal. `U` dibuang agar kata kasar tak terbentuk tak sengaja pada
 * stiker yang dilihat pelanggan.
 */
export const GUEST_QR_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const TOKEN_RE = new RegExp(`^[${GUEST_QR_ALPHABET}]{${GUEST_QR_TOKEN_LENGTH}}$`);
/** Guard bentuk token QR. Tak menyatakan token itu ADA — hanya bahwa ia layak dicari. */
export function isGuestQrToken(v) {
    return typeof v === 'string' && TOKEN_RE.test(v);
}
/**
 * URL yang dicetak pada stiker meja.
 *
 * ⭐ **Origin terpisah dari portal** (mis. `https://meja.osbpos.id`) — bukan kerapian, melainkan
 * satu-satunya batas yang ditegakkan browser: portal menyimpan token staf di `localStorage`,
 * halaman ini menerima teks bebas dari publik. Satu celah XSS di halaman tamu tak boleh menjangkau
 * token kasir.
 *
 * ⚠️ Kode tenant **terlihat** di URL, dan itu tak apa — namanya sudah tertempel di dinding
 * restoran. Yang menjaga adalah `token`, bukan kerahasiaan tenant; menebak tenant tanpa token tak
 * membuka apa pun. Ia ada di path karena K1 (DB-per-tenant): tanpanya, satu token harus dicari ke
 * seluruh database tenant.
 */
export function guestQrUrl(baseUrl, tenantCode, token) {
    return `${baseUrl.replace(/\/+$/, '')}/${tenantCode}/${token}`;
}
/** Rentang karakter kendali ASCII yang dibuang dari catatan tamu. */
const KENDALI = new RegExp(`[${String.fromCharCode(0)}-${String.fromCharCode(31)}${String.fromCharCode(127)}]`, 'g');
/**
 * Membersihkan catatan tamu.
 *
 * ⛔ Karakter kendali **dibuang, bukan di-escape**: teks ini tampil di HP pelayan dan (kelak) di
 * tiket dapur — dan byte kendali yang lolos ke printer termal bisa **mengubah perilaku printer**
 * (memotong kertas, membuka laci), bukan sekadar tampil aneh. Escape hanya menolong di HTML.
 *
 * ⚠️ Ini **BUKAN** penyaring kata kasar. Menyaring kata kasar dari teks bebas publik adalah
 * pekerjaan yang tak pernah selesai; yang menjaganya adalah batas laju, batas panjang, dan fakta
 * bahwa tiap panggilan **melekat pada satu meja yang bisa didatangi**.
 */
export function sanitizeGuestNote(raw) {
    return raw.replace(KENDALI, ' ').replace(/\s+/g, ' ').trim().slice(0, GUEST_NOTE_MAX);
}
/**
 * Status panggilan.
 *
 * | Nilai | Arti |
 * |---|---|
 * | `open` | menunggu pelayan |
 * | `acknowledged` | pelayan **menerima** — belum tentu sudah menghampiri |
 * | `cancelled` | dibatalkan (mis. meja ditutup sebelum didatangi) |
 * | `spam` | ⭐ pelayan menyatakan **bukan tamu** — lihat {@link GUEST_CALL_STATUSES} |
 * | `suppressed` | ⛔ sistem **meredam** karena meja ini sedang ditangguhkan |
 *
 * ### ⭐⭐ Kenapa `spam` diputuskan MANUSIA, dan `suppressed` diputuskan sistem
 * Tak ada cara teknis membedakan tamu asli dari orang jahil — keduanya mengirim permintaan yang
 * identik dari HP yang identik. Yang **bisa** membedakan hanyalah pelayan yang berdiri di lantai
 * dan melihat meja 7 kosong. ⇒ Sistem tak menebak; ia menyediakan tombol, lalu **menegakkan**
 * keputusan itu (`suppressed`) tanpa perlu ditanya lagi.
 *
 * ### ⛔ `suppressed` DIREKAM, dan tamu tetap melihat "terkirim"
 * Menolak dengan terang-terangan mengajari orang jahil mana yang berhasil — ia akan mencoba meja
 * lain sampai menemukan yang belum ditangguhkan. Diam membuatnya mengira berhasil dan berhenti.
 * ⚠️ Konsekuensinya: barisnya **tetap ditulis**, karena penangguhan yang tak meninggalkan bukti
 * adalah penangguhan yang tak bisa ditinjau manajer.
 */
export const GUEST_CALL_STATUSES = Object.freeze([
    'open', 'acknowledged', 'cancelled', 'spam', 'suppressed',
]);
//# sourceMappingURL=guest.js.map